var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useCallback, useEffect, useState } from 'react';
import { useTheme } from "../../hooks/useTheme";
import classNames from 'classnames';
import { makeStyles } from "../../styles/index";
import { useMeasure } from "../../hooks/useMeasure";
import { Search, Eye, EyeOff } from "../../icons/index";
import { Typography } from "../Typography/Typography";
var DEFAULT_MAX_LENGTH = 10000;
var styles = function (theme) { return ({
    // dom要素
    root: {},
    inputWrapper: {
        height: theme.spacing(7.5),
        width: 'auto',
        display: 'flex',
        justifyContent: 'center',
        borderRadius: theme.shape.borderRadius,
        borderWidth: theme.shape.borderWidth.normal,
        borderStyle: 'solid',
        outline: 'none',
    },
    input: {
        borderRadius: theme.shape.borderRadius,
        height: '100%',
        width: '100%',
        outline: 'none',
        border: 'none',
        backgroundColor: 'transparent',
        fontSize: '24px',
        padding: theme.spacing(0, 1),
        '&::-ms-reveal': {
            display: 'none',
        },
    },
    icon: {
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        marginLeft: theme.spacing(1),
    },
    // パスワード表示トグルアイコン
    passwordVisibleIcon: {
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        cursor: 'pointer',
        marginRight: theme.spacing(1),
    },
    validationMessage: {
        minHeight: theme.spacing(5),
        visibility: 'hidden',
        whiteSpace: 'nowrap',
    },
    // 状態
    light: {
        '& $inputWrapper': {
            // enabled
            backgroundColor: theme.palette.common.white,
            borderColor: theme.palette.common.black,
            color: theme.palette.common.black,
            '& $input': {
                color: theme.palette.light.text.primary,
                '&::placeholder': {
                    color: theme.palette.light.text.secondary,
                },
            },
            '&:hover': {
                borderColor: theme.palette.primary[200],
                backgroundColor: theme.palette.primary[50],
            },
            '&:focus-within': {
                borderColor: theme.palette.primary[300],
                backgroundColor: theme.palette.common.white,
            },
        },
        // disabled
        '&$disabled': {
            '& $inputWrapper': {
                borderColor: theme.palette.light.text.disabled,
                color: theme.palette.light.text.disabled,
                '& $input': {
                    color: theme.palette.light.text.disabled,
                    '&::placeholder': {
                        color: theme.palette.light.text.disabled,
                    },
                },
            },
            '& $passwordVisibleIcon': {
                color: theme.palette.light.text.disabled,
            },
        },
        '& $passwordVisibleIcon': {
            color: theme.palette.common.black,
        },
    },
    dark: {
        // enabled
        '& $inputWrapper': {
            backgroundColor: theme.palette.common.black,
            borderColor: theme.palette.common.white,
            color: theme.palette.common.white,
            '& $input': {
                color: theme.palette.common.white,
                '&::placeholder': {
                    color: theme.palette.dark.text.secondary,
                },
            },
            '&:hover': {
                borderColor: theme.palette.primary[200],
            },
            '&:focus-within': {
                borderColor: theme.palette.primary[300],
            },
        },
        // disabled
        '&$disabled': {
            '& $inputWrapper': {
                borderColor: theme.palette.dark.action.disabled,
                color: theme.palette.dark.text.disabled,
                '& $input': {
                    color: theme.palette.dark.text.disabled,
                    '&::placeholder': {
                        color: theme.palette.dark.text.disabled,
                    },
                },
            },
            '& $passwordVisibleIcon': {
                color: theme.palette.dark.action.disabled,
            },
        },
        '& $passwordVisibleIcon': {
            color: theme.palette.common.white,
        },
    },
    disabled: {
        '& $inputWrapper': {
            cursor: 'auto',
            pointerEvents: 'none',
        },
    },
    dense: {
        '& $inputWrapper': {
            height: theme.spacing(5),
            '& input': {
                fontSize: '18px',
            },
        },
    },
    invalid: {
        '& $validationMessage': {
            color: theme.palette.error.main,
            visibility: 'visible',
        },
    },
}); };
var useStyles = makeStyles(styles, { name: 'SnTextBox' });
export var TextField = React.forwardRef(function (props, ref) {
    var _a;
    var value = props.value, disabled = props.disabled, readonly = props.readonly, placeholder = props.placeholder, search = props.search, password = props.password, dense = props.dense, maxLength = props.maxLength, onInput = props.onInput, onChange = props.onChange, onKeyDown = props.onKeyDown, onFocus = props.onFocus, onBlur = props.onBlur, onClick = props.onClick, rules = props.rules, lazyValidation = props.lazyValidation, keepMessageSpace = props.keepMessageSpace, className = props.className, errorMessage = props.errorMessage, messageVariant = props.messageVariant, disableAutoComplete = props.disableAutoComplete, autoFocus = props.autoFocus;
    var classes = useStyles(props);
    var theme = useTheme(props);
    var _b = useState(value), lazyValue = _b[0], setLazyValue = _b[1];
    var _c = useState(''), lazyInnerErrorMessage = _c[0], setLazyInnerErrorMessage = _c[1];
    var _d = useState(''), innerErrorMessage = _d[0], setInnerErrorMessage = _d[1];
    var _e = useState(false), passwordVisible = _e[0], setPasswordVisible = _e[1];
    var rootClassName = classNames(classes.root, classes[theme.type], (_a = {},
        _a[classes.disabled] = disabled,
        _a[classes.dense] = dense,
        _a[classes.invalid] = errorMessage || (lazyValidation ? lazyInnerErrorMessage : innerErrorMessage),
        _a));
    var textFieldClassName = classNames(classes.inputWrapper, className);
    if (password && maxLength !== undefined) {
        throw new Error('passwordとmaxLengthを同時に使用しないでください。');
    }
    var validate = useCallback(function (inputValue) {
        var _a;
        // バリデーション項目が無い場合はundefinedを返す
        if (!rules) {
            return undefined;
        }
        return (_a = rules.find(function (rule) { return rule(inputValue) !== true; })) === null || _a === void 0 ? void 0 : _a(inputValue);
    }, [rules]);
    useEffect(function () {
        // 内部バリデーションでは初期値に対しバリデーションが行われないためここでチェックする
        if (!rules) {
            return;
        }
        var initValue = value || '';
        onInput === null || onInput === void 0 ? void 0 : onInput(initValue, validate(initValue));
    }, [onInput, rules, validate, value]);
    var handleChangeValue = function (event) {
        event.persist();
        if (event.currentTarget.value.length === 0) {
            // 再入力開始時に見えない様にする
            setPasswordVisible(false);
        }
        if (!rules) {
            onInput === null || onInput === void 0 ? void 0 : onInput(event.currentTarget.value);
            return;
        }
        var result = validate(event.currentTarget.value);
        setInnerErrorMessage(event.currentTarget.value ? result : undefined);
        onInput === null || onInput === void 0 ? void 0 : onInput(event.currentTarget.value, result);
    };
    var handleKeyDown = useCallback(function (e) {
        e.persist();
        if (e.key === 'Enter' || e.key === 'Tab') {
            if (rules && e.currentTarget.value !== lazyValue) {
                setLazyValue(e.currentTarget.value);
                setLazyInnerErrorMessage(e.currentTarget.value ? innerErrorMessage : undefined);
            }
            onChange === null || onChange === void 0 ? void 0 : onChange(e.currentTarget.value);
        }
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
    }, [onKeyDown, rules, lazyValue, onChange, innerErrorMessage]);
    var handleFocus = function (e) {
        e.persist();
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
    };
    var handleClick = function (e) {
        e.persist();
        onClick === null || onClick === void 0 ? void 0 : onClick(e);
    };
    var handleFocusOut = useCallback(function (e) {
        e.persist();
        if (rules && e.currentTarget.value !== lazyValue) {
            setLazyValue(e.currentTarget.value);
            setLazyInnerErrorMessage(e.currentTarget.value ? innerErrorMessage : undefined);
        }
        onChange === null || onChange === void 0 ? void 0 : onChange(e.currentTarget.value);
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
    }, [rules, lazyValue, onChange, onBlur, innerErrorMessage]);
    var handleClickPasswordVisibleIcon = useCallback(function (e) {
        if (!password) {
            // そもそもtypeがパスワードで無い場合は無視する
            return;
        }
        e.persist();
        setPasswordVisible(!passwordVisible);
    }, [password, passwordVisible]);
    var measureStyle = useMeasure(props).style;
    return (React.createElement("div", { className: rootClassName, style: __assign({}, measureStyle) },
        React.createElement("div", { className: textFieldClassName, onClick: handleClick },
            search && (React.createElement("div", { className: classes.icon },
                React.createElement(Search, { height: 32, width: 32 }))),
            React.createElement("input", { ref: ref, type: password && !passwordVisible ? 'password' : 'text', className: classes.input, value: value, placeholder: placeholder, maxLength: maxLength !== null && maxLength !== void 0 ? maxLength : DEFAULT_MAX_LENGTH, onChange: handleChangeValue, onKeyDown: handleKeyDown, onFocus: handleFocus, onBlur: handleFocusOut, disabled: disabled, readOnly: readonly, autoCapitalize: "off", autoComplete: disableAutoComplete ? 'new-password' : undefined, autoFocus: autoFocus }),
            password && !!value && (React.createElement("div", { className: classes.passwordVisibleIcon, onClick: handleClickPasswordVisibleIcon }, passwordVisible ? React.createElement(EyeOff, { height: 24, width: 24 }) : React.createElement(Eye, { height: 24, width: 24 })))),
        (errorMessage || rules || keepMessageSpace) && (React.createElement(Typography, { className: classes.validationMessage, variant: messageVariant !== null && messageVariant !== void 0 ? messageVariant : 'body2' }, errorMessage || (lazyValidation ? lazyInnerErrorMessage : innerErrorMessage)))));
});
